import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/auth';
import { switchWorkspace, useWorkspaceMemberships, WorkspaceMfaRequired } from '@/lib/workspace';
import { Button } from '@/components/ui/Button';

/** A notification may target a different workspace from the currently open tab. */
export function NotificationTenantGuard({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const targetTenant = new URLSearchParams(location.search).get('tenant');
  const memberships = useWorkspaceMemberships();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!targetTenant || targetTenant === session?.tenantId) return <>{children}</>;
  const target = memberships.data?.workspaces.find(workspace =>
    (workspace.tenant_id ?? workspace.id) === targetTenant && workspace.switchable !== false);
  const open = async () => {
    if (!target) return;
    setBusy(true); setError(null);
    try { await switchWorkspace(target.id); }
    catch (cause) {
      if (cause instanceof WorkspaceMfaRequired) navigate('/login?reauth=1', { state: { mfaChallenge:cause.challenge, from:location } });
      else setError(cause instanceof Error ? cause.message : 'ワークスペースを切り替えられませんでした。');
    } finally { setBusy(false); }
  };
  return <section className="m-6 space-y-4 rounded-lg border border-border-subtle bg-canvas p-6" aria-label="通知のワークスペース">
    <h1 className="text-lg font-semibold">別のワークスペースへの通知です</h1>
    {memberships.isPending ? <p role="status">参加しているワークスペースを確認しています…</p> : target ? <>
      <p>「{target.name}」へ切り替えて、通知のメッセージを開きます。</p>
      <Button disabled={busy} onClick={() => void open()}>{busy ? '切り替え中…' : '切り替えて開く'}</Button>
    </> : <p>このワークスペースへのアクセスを確認できません。参加しているアカウントでログインしてください。</p>}
    {(error || memberships.error) && <p role="alert" className="text-danger">{error || memberships.error?.message}</p>}
    <Button variant="secondary" onClick={() => navigate('/rooms')}>現在のワークスペースへ戻る</Button>
  </section>;
}
